import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug = 'romance' } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'popular' | 'newest' | 'rating'>('popular');

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryName = currentCategory?.name || '全部漫画';

  const filteredComics = useMemo(() => {
    let list = slug === 'completed'
      ? comics.filter((c) => c.status === 'completed')
      : comics.filter((c) => c.category === slug || slug === 'all');

    if (slug === 'all') list = [...comics];

    if (sortBy === 'popular') {
      list = list.sort((a, b) => b.views - a.views);
    } else if (sortBy === 'newest') {
      list = list.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    } else if (sortBy === 'rating') {
      list = list.sort((a, b) => b.rating - a.rating);
    }
    return list;
  }, [slug, sortBy]);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://gxzzmm66.cn/' },
      { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'http://gxzzmm66.cn/category/all' },
      { '@type': 'ListItem', position: 3, name: categoryName, item: `http://gxzzmm66.cn/category/${slug}` },
    ],
  };

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `${categoryName} - 韩漫网`,
    itemListElement: filteredComics.map((comic, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Book',
        name: comic.title,
        author: { '@type': 'Person', name: comic.author },
        description: comic.description,
        url: `http://gxzzmm66.cn/comic/${comic.id}`,
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: comic.rating,
          bestRating: 10,
        },
      },
    })),
  };

  return (
    <>
      <SEO
        title={`${categoryName} - 漫画分类`}
        description={`浏览${categoryName}分类下的全部韩国漫画，${filteredComics.length}部作品免费在线阅读。韩漫网提供最全面的漫画大全和最新更新。`}
        keywords={`${categoryName},韩漫${categoryName},韩国漫画${categoryName},免费${categoryName}漫画,在线阅读${categoryName}`}
        canonicalUrl={`http://gxzzmm66.cn/category/${slug}`}
        ogType="website"
        schemaData={{ ...breadcrumbSchema, ...itemListSchema }}
      />
      <Navbar />
      <main className="min-h-screen pt-16 bg-background-50">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto py-6">
          <nav aria-label="面包屑导航">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              <li>
                <Link to="/" className="hover:text-foreground-800 transition-colors cursor-pointer">首页</Link>
              </li>
              <li><div className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-foreground-400"></i></div></li>
              <li>
                <Link to="/category/all" className="hover:text-foreground-800 transition-colors cursor-pointer">分类浏览</Link>
              </li>
              <li><div className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-foreground-400"></i></div></li>
              <li className="text-foreground-800 font-medium">{categoryName}</li>
            </ol>
          </nav>
        </div>

        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar - Category Nav */}
            <aside className="lg:w-56 flex-shrink-0">
              <div className="bg-background-100 rounded-2xl p-5">
                <h3 className="text-sm font-semibold text-foreground-800 mb-4">全部分类</h3>
                <ul className="space-y-1">
                  <li>
                    <Link
                      to="/category/all"
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${
                        slug === 'all' ? 'bg-primary-100 text-primary-700 font-medium' : 'text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center"><i className="ri-apps-line text-sm"></i></div>
                      全部漫画
                      <span className="ml-auto text-xs text-foreground-400">{comics.length}</span>
                    </Link>
                  </li>
                  {categories.map((cat) => (
                    <li key={cat.slug}>
                      <Link
                        to={`/category/${cat.slug}`}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${
                          slug === cat.slug ? 'bg-primary-100 text-primary-700 font-medium' : 'text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        <div className="w-5 h-5 flex items-center justify-center"><i className={`${cat.icon} text-sm`}></i></div>
                        {cat.name}
                        <span className="ml-auto text-xs text-foreground-400">{cat.count}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">{categoryName}</h1>
                  <p className="text-sm text-foreground-500 mt-1">共收录 {filteredComics.length} 部作品</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-400">排序：</span>
                  {[
                    { key: 'popular', label: '人气' },
                    { key: 'newest', label: '最新' },
                    { key: 'rating', label: '评分' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key as typeof sortBy)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        sortBy === opt.key
                          ? 'bg-primary-500 text-white'
                          : 'bg-background-200 text-foreground-600 hover:bg-background-300'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Comic Grid */}
              {filteredComics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-5">
                  {filteredComics.map((comic) => (
                    <Link
                      key={comic.id}
                      to={`/comic/${comic.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3 bg-background-200">
                        <img
                          src={comic.coverImage}
                          alt={comic.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute top-2 left-2 flex items-center gap-1">
                          <span className="px-2 py-0.5 rounded bg-foreground-950/70 text-white text-[10px] font-medium">
                            {comic.status === 'completed' ? '已完结' : '连载中'}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="flex items-center gap-0.5 text-xs text-accent-600">
                          <i className="ri-star-fill text-[10px]"></i>
                          {comic.rating}
                        </span>
                        <span className="text-xs text-foreground-400">
                          {(comic.views / 10000).toFixed(0)}万阅
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
                    <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类暂无漫画</p>
                  <Link to="/category/all" className="inline-block mt-3 text-sm text-primary-500 hover:underline cursor-pointer">
                    查看全部漫画
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}